import React from 'react';
import { TFunction } from 'i18next';
import { RazmerRow } from '../components/OSRazmerTable';
import { parseDecimalInput } from '../utils/decimalInput';
import { formatRNDocumentError } from '../utils/rnDocumentError';
import { RNMaterialnoIssueRow, RNWorkOrderDoc } from './useRNWorkOrder';
import { DocumentType } from '../types/Types';
import { issueEntryRole, receiptEntryRole } from '../utils/workOrderRole';

interface FinishedProductRow {
  rowId?: string;
  itemId?: string;
  accountingTypeCode?: string;
  accountingTypeGroup?: string;
  measuredQty?: string;
  confirmed?: boolean;
}

interface UseRNProstDocumentActionsArgs {
  rnWorkOrder: RNWorkOrderDoc | null;
  razmerRows: RazmerRow[];
  finishedProductRows: FinishedProductRow[];
  isSaving: boolean;
  isDirty: boolean;
  // True when a batch/expiry suggestion is unsaved (screen-level, see RNWorkOrder.tsx).
  // Only the RECEIPT action (7200 Prost) below must wait for it — issuance (6421/6425
  // Razmer) doesn't read those saved values, so it stays gated on real isDirty only.
  hasUnsavedSuggestions: boolean;
  documentTypes: DocumentType[];
  issueMaterial6421Razmer: (rnId: string, rows: RNMaterialnoIssueRow[]) => Promise<{ document?: { number?: string } }>;
  issueMaterial6425Razmer: (rnId: string) => Promise<{ document?: { number?: string } }>;
  receiveFinishedProduct7200Prost: (rnId: string) => Promise<{ document?: { number?: string } }>;
  saveBeforeReceive: () => Promise<boolean>;
  showToast: (message: string, type: 'success' | 'error') => void;
  t: TFunction;
}

export const useRNProstDocumentActions = ({
  rnWorkOrder,
  razmerRows,
  finishedProductRows,
  isSaving,
  isDirty,
  hasUnsavedSuggestions,
  documentTypes,
  issueMaterial6421Razmer,
  issueMaterial6425Razmer,
  receiveFinishedProduct7200Prost,
  saveBeforeReceive,
  showToast,
  t,
}: UseRNProstDocumentActionsArgs) => {
  const [isPending, setIsPending] = React.useState(false);
  const pendingRef = React.useRef(false);

  const issueRows = React.useMemo(() => razmerRows
    .filter((row) => row.osRowId && row.itemId && row.confirmed
      && (parseDecimalInput(row.measuredQty) ?? 0) > 0)
    .map((row) => ({
      rowKey: row.osRowId!,
      item: row.itemId,
      ident: row.ident,
      name: row.name,
      unitCode: row.unitCode,
    })), [razmerRows]);

  const materialnoIssue = React.useMemo(
    () => (rnWorkOrder?.materialnoIssues || []).find((issue) => issueEntryRole(issue, documentTypes) === 'issue-materials'),
    [rnWorkOrder?.materialnoIssues, documentTypes]
  );
  const materialnoIssueGp = React.useMemo(
    () => (rnWorkOrder?.materialnoIssues || []).find((issue) => issueEntryRole(issue, documentTypes) === 'issue-finished'),
    [rnWorkOrder?.materialnoIssues, documentTypes]
  );
  const finishedReceipt = React.useMemo(
    () => (rnWorkOrder?.interProductReceipts || []).find((receipt) => receiptEntryRole(receipt, documentTypes) === 'receive-finished'),
    [rnWorkOrder?.interProductReceipts, documentTypes]
  );
  const hasFinishedProduct = React.useMemo(() => finishedProductRows.some((row) =>
    row.rowId?.startsWith('finished:') && row.itemId
      && row.confirmed && (parseDecimalInput(row.measuredQty) ?? 0) > 0
  ), [finishedProductRows]);

  // The 7200 Prost route reads saved actuals, so handleReceive saves pending edits first.

  const issueReason = materialnoIssue ? 'AlreadyIssued'
    : !rnWorkOrder?._id ? 'Unsaved'
      : isDirty ? 'Dirty'
        : isSaving || isPending ? 'Saving'
          : issueRows.length === 0 ? 'NoRows' : 'Tooltip';
  const receiveReason = finishedReceipt ? 'AlreadyReceived'
    : !rnWorkOrder?._id ? 'Unsaved'
      : isDirty ? 'Dirty'
        : hasUnsavedSuggestions ? 'Suggestions'
          : isSaving || isPending ? 'Saving'
        : !hasFinishedProduct ? 'NoRows' : 'Tooltip';
  // No client-side row check: the client's item cache can hold a partial ident and does
  // not reliably know the accounting type. The server derives the finished-product rows
  // and returns a clear 400 when there are none.
  const issueGpReason = materialnoIssueGp ? 'AlreadyIssued'
    : !rnWorkOrder?._id ? 'Unsaved'
      : isDirty ? 'Dirty'
        : isSaving || isPending ? 'Saving' : 'Tooltip';

  const handleIssue = React.useCallback(async () => {
    if (pendingRef.current || issueReason !== 'Tooltip' || !rnWorkOrder?._id) return;
    pendingRef.current = true;
    setIsPending(true);
    try {
      const result = await issueMaterial6421Razmer(rnWorkOrder._id, issueRows);
      showToast(t('os.materialno.issue6421Success', { number: result.document?.number || '' }), 'success');
    } catch (err: any) {
      const data = err?.response?.data;
      showToast(formatRNDocumentError(data, t, 'os.materialno.issue6421Failed'), 'error');
    } finally {
      pendingRef.current = false;
      setIsPending(false);
    }
  }, [issueMaterial6421Razmer, issueReason, issueRows, rnWorkOrder?._id, showToast, t]);

  const handleIssueGp = React.useCallback(async () => {
    if (pendingRef.current || issueGpReason !== 'Tooltip' || !rnWorkOrder?._id) return;
    pendingRef.current = true;
    setIsPending(true);
    try {
      const result = await issueMaterial6425Razmer(rnWorkOrder._id);
      showToast(t('os.materialno.issue6425Success', { number: result.document?.number || '' }), 'success');
    } catch (err: any) {
      const data = err?.response?.data;
      showToast(formatRNDocumentError(data, t, 'os.materialno.issue6425Failed'), 'error');
    } finally {
      pendingRef.current = false;
      setIsPending(false);
    }
  }, [issueMaterial6425Razmer, issueGpReason, rnWorkOrder?._id, showToast, t]);

  const handleReceive = React.useCallback(async () => {
    if (pendingRef.current || receiveReason !== 'Tooltip' || !rnWorkOrder?._id) return;
    pendingRef.current = true;
    setIsPending(true);
    try {
      if ((isDirty || hasUnsavedSuggestions) && !(await saveBeforeReceive())) return;
      const result = await receiveFinishedProduct7200Prost(rnWorkOrder._id);
      showToast(t('os.materialno.receive7200Success', { number: result.document?.number || '' }), 'success');
    } catch (err: any) {
      const data = err?.response?.data;
      showToast(formatRNDocumentError(data, t, 'os.materialno.receive7200Failed'), 'error');
    } finally {
      pendingRef.current = false;
      setIsPending(false);
    }
  }, [hasUnsavedSuggestions, isDirty, receiveFinishedProduct7200Prost, receiveReason, rnWorkOrder?._id, saveBeforeReceive, showToast, t]);

  return {
    issueDisabled: issueReason !== 'Tooltip',
    issueTitle: t(`os.materialno.issue6421${issueReason}`, { number: materialnoIssue?.documentNumber || '' }),
    receiveDisabled: receiveReason !== 'Tooltip',
    receiveTitle: t(`os.materialno.receive7200${receiveReason}`, { number: finishedReceipt?.documentNumber || '' }),
    issueGpDisabled: issueGpReason !== 'Tooltip',
    issueGpTitle: t(`os.materialno.issue6425${issueGpReason}`, { number: materialnoIssueGp?.documentNumber || '' }),
    handleIssue,
    handleReceive,
    handleIssueGp,
  };
};
